import { Button, FormControl, MenuItem, Select, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';
import { Dialog, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import type { StudioAction, StudioUrlAction } from '../../context';
import { StudioSidebarTitle } from '../lib';

export interface StudioInteractionDialogProps {
  action?: StudioAction;
  onClose: () => void;
  onSave: (action?: StudioAction) => void;
  open: boolean;
}

const METHODS: StudioUrlAction['method'][] = ['GET', 'POST'];

export const StudioInteractionDialog = ({
  action,
  onClose,
  onSave,
  open,
}: StudioInteractionDialogProps) => {
  const { t } = useTranslation();

  const formik = useFormik({
    initialValues: {
      method: action?.method ?? 'GET',
      type: action?.type ?? 'none',
      url: action?.url ?? '',
    },
    onSubmit: (values) => {
      onSave(
        values.type === 'url'
          ? { method: values.method, type: 'url', url: values.url.trim() }
          : undefined,
      );
      onClose();
    },
    enableReinitialize: true,
    validateOnBlur: true,
    validationSchema: object({
      url: string().when('type', {
        is: 'url',
        then: (schema) => schema.required().trim(),
      }),
    }),
  });

  const cancel = () => {
    onClose();
    formik.resetForm();
  };

  return (
    <Dialog open={open} onClose={cancel} maxWidth="sm" fullWidth>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle onClose={cancel}>
          {t('pages.studio.edit.settings.interactions.on_click')}
        </DialogTitle>

        <DialogContent>
          <StudioSidebarTitle>
            {t('pages.studio.edit.settings.interactions.action')}
          </StudioSidebarTitle>

          <FormControl fullWidth margin="dense">
            <Select
              name="type"
              onChange={formik.handleChange}
              value={formik.values.type}
              variant="outlined"
            >
              <MenuItem value="none">
                {t('pages.studio.edit.settings.interactions.types.none')}
              </MenuItem>
              <MenuItem value="url">
                {t('pages.studio.edit.settings.interactions.types.url')}
              </MenuItem>
            </Select>
          </FormControl>

          {formik.values.type === 'url' && (
            <>
              <StudioSidebarTitle>
                {t('pages.studio.edit.settings.interactions.url')}
              </StudioSidebarTitle>

              <FormControl fullWidth margin="dense">
                <TextField
                  autoFocus
                  error={formik.touched.url && Boolean(formik.errors.url)}
                  helperText={formik.touched.url && formik.errors.url}
                  name="url"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  placeholder={t('pages.studio.edit.settings.interactions.url_placeholder')}
                  value={formik.values.url}
                  variant="outlined"
                />
              </FormControl>

              <StudioSidebarTitle>
                {t('pages.studio.edit.settings.interactions.method')}
              </StudioSidebarTitle>

              <FormControl fullWidth margin="dense">
                <Select
                  name="method"
                  onChange={formik.handleChange}
                  value={formik.values.method}
                  variant="outlined"
                >
                  {METHODS.map((method) => (
                    <MenuItem key={method} value={method}>
                      {method}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={cancel}>
            {t('common.dialogs.cancel')}
          </Button>
          <Button color="primary" disabled={!formik.isValid} type="submit" variant="contained">
            {t('common.dialogs.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
